/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/execution/components/ChecklistExecutionPanel.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Execution Workspace
 *
 * DESCRIPTION:
 * Displays all checklist sections for the work execution.
 *
 * RESPONSIBILITIES
 * ----------------
 * ✓ Display checklist summary
 * ✓ Render checklist sections
 * ✓ Expand / Collapse All
 * ✓ Delegate events to ChecklistSection
 *
 ******************************************************************************/

import React, { useMemo, useState } from "react";
import ChecklistSection from "./ChecklistSection";

export default function ChecklistExecutionPanel({

    checklist = [],

    disabled = false,

    onResponseChange,

    onRemarksChange,

    onPhotoClick,

    onAttachmentClick,

    onObservationClick,

}) {

    // ==========================================================
    // STATE
    // ==========================================================

    const [expandAll, setExpandAll] = useState(true);

    // ==========================================================
    // SUMMARY
    // ==========================================================

    const summary = useMemo(() => {

        let totalSections = checklist.length;
        let totalItems = 0;
        let completedItems = 0;
        let mandatoryPending = 0;

        checklist.forEach(section => {

            totalItems += section.items.length;

            section.items.forEach(item => {

                if (item.completed) {

                    completedItems++;

                }

                if (item.mandatory && !item.completed) {

                    mandatoryPending++;

                }

            });

        });

        return {

            totalSections,

            totalItems,

            completedItems,

            mandatoryPending,

            percentage:

                totalItems === 0

                    ? 0

                    : Math.round(

                        (completedItems / totalItems) * 100

                    ),

        };

    }, [checklist]);

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <div className="card shadow-sm border-0 mb-4">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <div className="card-header bg-white">

                <div className="d-flex justify-content-between align-items-center">

                    <div>

                        <h5 className="mb-1">

                            Checklist Execution

                        </h5>

                        <small className="text-muted">

                            Execute each checklist item and provide required evidence.

                        </small>

                    </div>

                    <button

                        type="button"

                        className="btn btn-outline-primary btn-sm"

                        onClick={() => setExpandAll(!expandAll)}

                    >

                        <i className={`fas ${expandAll ? "fa-compress-alt" : "fa-expand-alt"} me-2`} />

                        {expandAll ? "Collapse All" : "Expand All"}

                    </button>

                </div>

            </div>

            {/* ====================================================== */}
            {/* SUMMARY */}
            {/* ====================================================== */}

            <div className="card-body border-bottom">

                <div className="row text-center">

                    <div className="col-md-3">

                        <h5 className="mb-1">

                            {summary.totalSections}

                        </h5>

                        <small className="text-muted">

                            Sections

                        </small>

                    </div>

                    <div className="col-md-3">

                        <h5 className="mb-1">

                            {summary.completedItems} / {summary.totalItems}

                        </h5>

                        <small className="text-muted">

                            Completed

                        </small>

                    </div>

                    <div className="col-md-3">

                        <h5 className="mb-1 text-danger">

                            {summary.mandatoryPending}

                        </h5>

                        <small className="text-muted">

                            Mandatory Pending

                        </small>

                    </div>

                    <div className="col-md-3">

                        <h5 className="mb-1 text-success">

                            {summary.percentage}%

                        </h5>

                        <small className="text-muted">

                            Progress

                        </small>

                    </div>

                </div>

            </div>

            {/* ====================================================== */}
            {/* SECTIONS */}
            {/* ====================================================== */}

            <div className="card-body">

                {

                    checklist.map((section) => (

                        <ChecklistSection

                            key={section.id}

                            section={{

                                ...section,

                                expanded: expandAll,

                            }}

                            disabled={disabled}

                            onResponseChange={onResponseChange}

                            onRemarksChange={onRemarksChange}

                            onPhotoClick={onPhotoClick}

                            onAttachmentClick={onAttachmentClick}

                            onObservationClick={onObservationClick}

                        />

                    ))

                }

            </div>

        </div>

    );

}